/**
 * Browser Google Maps key, fetched from dispatcher_bff instead of being baked
 * into the bundle at build time.
 *
 * WHY. `VITE_GOOGLE_MAPS_API_KEY` shipped the key inside a publicly downloadable
 * JS bundle, so anyone who could fetch the SPA could harvest it without signing
 * in, and rotating it meant rebuilding and redeploying every environment. This
 * endpoint is session-guarded, so only an authenticated dispatcher receives it,
 * and rotation is a BFF restart.
 *
 * WHAT IT IS NOT. The Maps JS SDK runs in the page, so the key still reaches the
 * browser and any signed-in user can read it from devtools. This is not secrecy
 * and does not replace the Google Cloud HTTP-referrer allowlist, API allowlist
 * and quota cap — those remain the control on abuse and must stay configured.
 *
 * SAME-ORIGIN. `/drt/api/*` is already proxied to the BFF, so no backend URL is
 * embedded here. The dispatcher MFE calls this exact path for the same reason.
 *
 * Deliberately plain `fetch`, not the shared axios client: that client's request
 * interceptor attaches `X-Geo-Token` for the maps proxy and its response
 * interceptor drives session-expiry handling. Neither applies to a cookie-gated
 * config read that runs before the map exists, and routing this through it would
 * couple SDK bootstrap to interceptor behaviour for no gain.
 */
export interface MapsConfig {
  googleMapsApiKey: string;
}

/** Validates the wire shape before a key is handed to the Google loader. */
export function parseMapsConfig(raw: unknown): MapsConfig {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    throw new Error('Map configuration is invalid.');
  }
  const key = (raw as Record<string, unknown>).googleMapsApiKey;
  // Google keys are URL-safe base64-ish. Rejecting anything else here stops a
  // stray HTML error page (a proxy that answers 200 with index.html, say) from
  // being interpolated into a <script src> as though it were a key.
  if (typeof key !== 'string' || !/^[A-Za-z0-9_-]{1,256}$/.test(key.trim())) {
    throw new Error('Map configuration is invalid.');
  }
  return { googleMapsApiKey: key.trim() };
}

export async function fetchMapsConfig(signal?: AbortSignal): Promise<MapsConfig> {
  const response = await fetch('/drt/api/bff/maps-config', {
    // Sends dispatcher_bff_sid. Without it the BFF's SessionGuard answers 401.
    credentials: 'same-origin',
    cache: 'no-store',
    ...(signal ? { signal } : {}),
  });
  if (!response.ok) {
    // Never surface the upstream body: it is a config endpoint and its errors
    // can name server-side variables.
    throw new Error('Map configuration is temporarily unavailable.');
  }
  return parseMapsConfig(await response.json());
}
